// ============================================================
// HOME — 朝のダイジェスト。「開いた瞬間に今日やることが分かる」入口。
//   上段: 挨拶（今日の予定/未対応数）
//   中段: ミニカレンダー（日/週/月切替・既定=週・予定/Google予定/自分の期限・クリックで/calendar）
//         ＋右カラム: 自分のToDo（クイック追加つき・/tasks と同じ挙動）
//                     ＋チームタスク（直近1週間・日付グルーピング・期限超過を先頭）
// Google予定は GoogleDataContext（ログイン時ブートストラップ取得）から消費し、
// 週チップ/月ドットに会議系（sky）でマージ（タイトル+日付+時刻の一致で dedupe）。
//   下段: 新着議事録・プロジェクト進捗（KGI/KPI）・モジュールショートカット
// tasks / meetingNotes / calendarEvents は getAdapter() 経由（live は
// Firestore クエリが viewer 権限でスコープ済み・mock はここで permissions 相当のフィルタを掛ける）。
// tasks / meetingNotes は改善④でリアルタイム購読（他人の更新が再読み込みなしで反映）。
// 旧データ・空配列でもクラッシュしない（loading/空ガード）。
// ============================================================
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useViewer } from '../auth/AuthContext';
import { getAdapter } from '../data/adapter';
import {
  visibleProjects,
  visibleCalendar,
  myTasks,
  isAdmin,
  isExec,
  kpiAchievement,
} from '../lib/permissions';
import { useGoogleData } from '../lib/GoogleDataContext';
import { TODAY, WEEKDAY_JP, CAL_KIND, SOURCE_META } from '../data/constants';
import { PROJECT_MAP, MEMBER_MAP } from '../data/demoData';
import { Icon } from '../components/Icons';
import { Avatar, ProjectBadge } from '../components/Badges';
import MeetImportPanel from '../components/MeetImportPanel';
import type { CalendarEvent, MeetingNote, Task, SynthesizedCalendarEntry } from '../types/models';

const SHORTCUTS = [
  { to: '/notes', label: '議事録', desc: '会議の要約と決定事項' },
  { to: '/tasks', label: 'マイタスク', desc: '自分の担当を一覧で' },
  { to: '/calendar', label: 'カレンダー', desc: '会議・締切・予定' },
  { to: '/projects', label: 'プロジェクト', desc: 'PJ進捗とKPI' },
  { to: '/knowledge', label: 'ナレッジ', desc: 'AX事例・プロンプト・手順' },
];

/** 自分のタスク期限をカレンダーに落とすときのドット色（charcoal・tokens準拠）。 */
const MY_DUE_DOT = '#1F2937';
/** 週表示ミニチップの自分タスク色（charcoal地×白字）。 */
const MY_DUE_CHIP = { bg: '#1F2937', fg: '#FFFFFF' };
/** カレンダー日/週/月切替の保存キー（localStorage・次回訪問時も維持）。 */
const CAL_VIEW_KEY = 'home-cal-view';
/** ミニカレンダーのビュー種別。既定=週・localStorage の不正値は 'week' に倒す。 */
type CalView = 'day' | 'week' | 'month';

function todayLabel(): string {
  const d = new Date(TODAY + 'T00:00:00+09:00');
  const wd = WEEKDAY_JP[d.getDay()];
  return `${TODAY.replace(/-/g, '/')}（${wd}）`;
}

/** 'YYYY-MM-DD' に n 日足した 'YYYY-MM-DD'（タイムゾーン非依存の素のJS計算）。 */
function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  const p = (x: number) => String(x).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

/** 'YYYY-MM-DD' → '7/9（木）'。チームタスクの日付見出し用（今日は呼び出し側で「今日」表記）。 */
function dateHeading(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${m}/${d}（${WEEKDAY_JP[dow]}）`;
}

/** 当月のカレンダーグリッド（日曜起点）。月外は空セル（null）で埋める。 */
function monthCells(): (string | null)[] {
  const [y, m] = TODAY.split('-').map(Number);
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); // 0=日
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const p = (x: number) => String(x).padStart(2, '0');
  const cells: (string | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${y}-${p(m)}-${p(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function SectionCard({
  title,
  to,
  toLabel,
  headerExtra,
  children,
}: {
  title: string;
  to?: string;
  toLabel?: string;
  /** ヘッダ右（「すべて見る」の左）に差し込む小さなコントロール（週/月トグル等）。 */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-card border border-hair bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold text-charcoal">{title}</h2>
        <div className="flex items-center gap-3">
          {headerExtra}
          {to && (
            <Link to={to} className="text-xs font-medium text-sky-deep hover:underline">
              {toLabel || 'すべて見る'}
            </Link>
          )}
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-xs text-slate-sub py-2">{text}</p>;
}

/** チームタスクの1行（担当アバター＋タイトル＋PJバッジ）。表示のみ・クリックなし。 */
function TeamTaskRow({ task, overdue }: { task: Task; overdue?: boolean }) {
  return (
    <li className="flex items-center gap-2.5 min-w-0">
      <Avatar memberId={task.assignee} size={22} />
      <span className={`text-sm truncate ${overdue ? 'text-danger-deep' : 'text-charcoal'}`}>{task.title}</span>
      <span className="ml-auto shrink-0">
        <ProjectBadge projectId={task.projectId} />
      </span>
    </li>
  );
}

export default function Home() {
  const viewer = useViewer();
  const { uid } = useAuth();
  // Google 予定（ログイン時に GoogleDataProvider がブートストラップ取得済み・未接続は []）。
  const { googleEvents } = useGoogleData();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [notesRaw, setNotesRaw] = useState<MeetingNote[] | null>(null);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickBusy, setQuickBusy] = useState(false);
  // カレンダー日/週/月切替（既定=週）。localStorage に保存して次回訪問時も維持する。
  // 既存ガードを踏襲：既知の値（day/month）だけ採用し、不正値・未保存は 'week' に倒す。
  const [calView, setCalView] = useState<CalView>(() => {
    try {
      const v = localStorage.getItem(CAL_VIEW_KEY);
      return v === 'month' || v === 'day' ? v : 'week';
    } catch {
      return 'week';
    }
  });
  const switchCalView = (v: CalView) => {
    setCalView(v);
    try {
      localStorage.setItem(CAL_VIEW_KEY, v);
    } catch {
      /* localStorage 不可環境では保存されないだけ（切替自体は効く） */
    }
  };

  // 管理者/役員 = チーム横断ビュー（live の getTasks も crossRead で全PJスコープになる）。
  const execView = isAdmin(viewer) || isExec(viewer);

  useEffect(() => {
    let alive = true;
    getAdapter()
      .then((a) => Promise.all([a.getTasks(), a.getCalendarEvents()]))
      .then(([t, ev]) => {
        if (!alive) return;
        // tasks は下の subscribeTasks が正になる。購読の初回 emit が先に届いていたら
        // 古い get 結果で上書きしない（events は購読対象外なので従来どおりセット）。
        setTasks((prev) => prev ?? t);
        setEvents(ev);
      })
      .catch(() => {
        if (alive) {
          setTasks((prev) => prev ?? []); // 取得失敗でも UI は空表示で耐える
          setEvents([]);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  // 改善④: tasks をリアルタイム購読（チームタスク・自分のToDo が他人の更新でも再読み込み不要に）。
  //   初期 get は上で残す＝購読の確立に失敗しても既存の get 結果で画面が成立する（購読エラーは
  //   adapter 側で console.warn 握りつぶし）。mock は即時1回だけ流れる（DEV挙動は実質不変）。
  //   calendarEvents は対象外（従来どおり get のまま）。
  useEffect(() => {
    let alive = true;
    let unsub: (() => void) | null = null;
    void getAdapter().then((a) => {
      if (!alive) return;
      unsub = a.subscribeTasks((t) => setTasks(t));
    });
    return () => {
      alive = false;
      if (unsub) unsub(); // unmount で必ず購読解除
    };
  }, []);

  // 「新着の議事録」用：初期 get（購読失敗時の保険）＋ subscribeNotes のリアルタイム購読
  //   （NotesList と同じ流儀）。購読の初回 emit が先に届いていたら古い get 結果で上書きしない。
  useEffect(() => {
    let alive = true;
    getAdapter()
      .then((a) => a.getNotes())
      .then((n) => {
        if (alive) setNotesRaw((prev) => prev ?? n);
      })
      .catch(() => {
        if (alive) setNotesRaw((prev) => prev ?? []); // 取得失敗でも空表示で耐える
      });
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    let alive = true;
    let unsub: (() => void) | null = null;
    void getAdapter().then((a) => {
      if (!alive) return;
      unsub = a.subscribeNotes((n) => setNotesRaw(n));
    });
    return () => {
      alive = false;
      if (unsub) unsub(); // unmount で必ず購読解除
    };
  }, []);

  // ── derived (viewer / tasks に依存) ──
  // 合成カレンダー（live議事録の会議＋自分の期限＋live予定）は挨拶の「今日の予定 N件」に使う。
  // 静的デモデータではなく取得済みの live データを渡す（ミニカレンダー表示と同じソース）。
  const calendar = useMemo<SynthesizedCalendarEntry[]>(
    () =>
      visibleCalendar(viewer, {
        tasks: tasks ?? [],
        notes: notesRaw ?? [],
        events: events ?? [],
        uid,
      }),
    [viewer, tasks, notesRaw, events, uid],
  );

  // 新着の議事録（直近4件）。フィルタは NotesList と同じ思想：役員/admin＝全PJ分・一般＝自分のPJのみ。
  const recentNotes = useMemo(
    () =>
      (notesRaw ?? [])
        .filter((n) => execView || viewer.projects.includes(n.projectId))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 4),
    [notesRaw, viewer, execView],
  );

  const projects = useMemo(() => visibleProjects(viewer).slice(0, 4), [viewer]);

  const openCount = useMemo(() => {
    if (!tasks) return 0;
    return myTasks(viewer, tasks).filter((t) => t.status !== 'done').length;
  }, [viewer, tasks]);

  // 期限が今日/超過の自分タスク数（朝の「今すぐ片付けるべき数」）。
  // 期限なし（due=''）は '' <= TODAY が常に true になるため除外する。
  const dueTodayCount = useMemo(() => {
    if (!tasks) return 0;
    return myTasks(viewer, tasks).filter((t) => t.status !== 'done' && !!t.due && t.due <= TODAY).length;
  }, [viewer, tasks]);

  // ── Google 予定の重複除去キー ──
  // adapter 由来の予定（可視分）と「タイトル+日付+時刻」が一致する Google 予定はスキップする
  // （ポータル側にも登録済みの会議が二重表示されるのを防ぐ。Calendar.tsx の id-dedupe は
  //   ソースを跨ぐと効かないため、ここでは単純一致で畳む）。
  // 個人予定の ownerId は Auth UID で保存される（Calendar.tsx の保存処理）ため uid で照合する。
  const adapterEventKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const e of events || []) {
      if (e.ownerId && e.ownerId !== uid) continue;
      if (e.projectId && !viewer.projects.includes(e.projectId)) continue;
      keys.add(e.title + '|' + e.date + '|' + (e.time || ''));
    }
    return keys;
  }, [events, viewer, uid]);
  // 表示対象の Google 予定（自分の primary カレンダー由来＝権限フィルタ不要・dedupe のみ）。
  const visibleGoogleEvents = useMemo(
    () => googleEvents.filter((g) => !adapterEventKeys.has(g.title + '|' + g.date + '|' + (g.time || ''))),
    [googleEvents, adapterEventKeys],
  );

  // 挨拶の「今日の予定 N件」＝ live 合成（会議・自分の期限・予定）＋ Google 予定（dedupe 済み）。
  // ミニカレンダーと同じ live ソースから数えるため、表示と件数がズレない。
  const todayEvents = useMemo(
    () => [
      ...calendar.filter((e) => e.date === TODAY),
      ...visibleGoogleEvents.filter((g) => g.date === TODAY),
    ],
    [calendar, visibleGoogleEvents],
  );

  // ── ミニ月間カレンダー：日付→ドット色（1件=1ドット・表示は最大5＋「+N」） ──
  // 旧仕様は「同色は畳む・最大3」だったが、表示数を増やす改修（2026-07-10）で
  // 件数がそのまま見えるよう畳みをやめた。描画側で slice(0,5)＋超過は「+N」。
  const cells = useMemo(() => monthCells(), []);
  const dayDots = useMemo(() => {
    const map = new Map<string, string[]>();
    const push = (ymd: string, color: string) => {
      if (!ymd) return;
      const arr = map.get(ymd) || [];
      arr.push(color);
      map.set(ymd, arr);
    };
    // 予定（kind別色）。個人予定は本人のみ（ownerId は Auth UID で保存＝uid で照合）・
    // PJ紐付きは自分のPJのみ（Calendar画面と同じ考え方）。
    for (const e of events || []) {
      if (e.ownerId && e.ownerId !== uid) continue;
      if (e.projectId && !viewer.projects.includes(e.projectId)) continue;
      push(e.date, CAL_KIND[e.kind]?.dot || '#94A3B8');
    }
    // Google カレンダー予定（会議系＝sky ドット。自分の primary 由来なので権限フィルタ不要）。
    for (const g of visibleGoogleEvents) {
      push(g.date, CAL_KIND.meeting.dot);
    }
    // 自分の未完了タスクの期限（charcoalドット）。
    for (const t of tasks || []) {
      if (t.assignee !== viewer.id || t.status === 'done' || !t.due) continue;
      push(t.due, MY_DUE_DOT);
    }
    return map;
  }, [events, tasks, viewer, uid, visibleGoogleEvents]);

  // ── 週表示：今週（日曜起点・TODAY含む）7日と、日付→ミニチップ（タイトル＋色） ──
  const weekDays = useMemo(() => {
    const [y, m, d] = TODAY.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=日
    return Array.from({ length: 7 }, (_, i) => addDays(TODAY, i - dow));
  }, []);
  const dayItems = useMemo(() => {
    const map = new Map<string, { title: string; bg: string; fg: string; time?: string }[]>();
    const push = (ymd: string, item: { title: string; bg: string; fg: string; time?: string }) => {
      if (!ymd) return;
      const arr = map.get(ymd) || [];
      arr.push(item);
      map.set(ymd, arr);
    };
    // 月表示のドットと同じ可視ソース：予定（kind別色・個人予定は uid 照合）＋自分の未完了タスク期限。
    for (const e of events || []) {
      if (e.ownerId && e.ownerId !== uid) continue;
      if (e.projectId && !viewer.projects.includes(e.projectId)) continue;
      const k = CAL_KIND[e.kind];
      push(e.date, { title: e.title, bg: k?.bg || '#F3F4F6', fg: k?.fg || '#374151', time: e.time });
    }
    // Google カレンダー予定（会議系＝sky チップ。Calendar.tsx と同じ SynthesizedCalendarEntry を
    // タイトル/日付/時刻で表示に落とす。dedupe 済み・時刻順ソートにも乗る）。
    for (const g of visibleGoogleEvents) {
      push(g.date, { title: g.title, bg: CAL_KIND.meeting.bg, fg: CAL_KIND.meeting.fg, time: g.time });
    }
    for (const t of tasks || []) {
      if (t.assignee !== viewer.id || t.status === 'done' || !t.due) continue;
      push(t.due, { title: t.title, bg: MY_DUE_CHIP.bg, fg: MY_DUE_CHIP.fg });
    }
    // 各日のチップを時刻順に並べる：時刻を持つ予定→時刻昇順、終日予定・タスクは後ろ（元の順を維持）。
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.time && b.time ? a.time.localeCompare(b.time) : a.time ? -1 : b.time ? 1 : 0));
    }
    return map;
  }, [events, tasks, viewer, uid, visibleGoogleEvents]);

  // ── 日表示：今日1日分のアイテム（週チップと同じソース・同じソート＝終日は末尾） ──
  const todayItems = useMemo(() => dayItems.get(TODAY) || [], [dayItems]);

  // ── チームタスク：未完了×期限が今日〜7日後（＋期限超過は先頭グループ） ──
  const teamTasks = useMemo(() => {
    if (!tasks) return { overdue: [] as Task[], byDate: [] as { date: string; items: Task[] }[] };
    const end = addDays(TODAY, 7);
    const visible = tasks.filter((t) => {
      if (t.status === 'done' || !t.due || t.due > end) return false;
      if (t.assignee === viewer.id) return true; // 自分担当は未分類含め表示
      if (!t.projectId) return false; // 他人の未分類（個人ToDo）は出さない
      return execView || viewer.projects.includes(t.projectId); // 役員=全PJ／一般=自分のPJ
    });
    const overdue = visible.filter((t) => t.due < TODAY).sort((a, b) => a.due.localeCompare(b.due));
    const upcoming = visible.filter((t) => t.due >= TODAY).sort((a, b) => a.due.localeCompare(b.due));
    const byDate: { date: string; items: Task[] }[] = [];
    for (const t of upcoming) {
      const last = byDate[byDate.length - 1];
      if (last && last.date === t.due) last.items.push(t);
      else byDate.push({ date: t.due, items: [t] });
    }
    return { overdue, byDate };
  }, [tasks, viewer, execView]);

  // ── 自分のToDo：未完了を due 昇順（無期限は最後）で最大5件 ──
  const myOpen = useMemo(() => {
    if (!tasks) return [];
    return tasks
      .filter((t) => t.assignee === viewer.id && t.status !== 'done')
      .sort((a, b) => {
        if (!a.due && !b.due) return 0;
        if (!a.due) return 1;
        if (!b.due) return -1;
        return a.due.localeCompare(b.due);
      })
      .slice(0, 5);
  }, [tasks, viewer]);

  // クイック追加：/tasks（MyTasks）の addQuickTask と同じ流儀。trim→createTask（未分類・本人限定）
  // →ローカル即反映→クリア。ホームでは Google runSync は呼ばない（次回 /tasks 表示時に同期される）。
  const addQuickTask = async () => {
    const title = quickTitle.trim();
    if (!title || quickBusy) return; // 空 or 実行中はスキップ
    setQuickBusy(true);
    try {
      const adapter = await getAdapter();
      const created = await adapter.createTask({
        title,
        assignee: viewer.id, // 自分担当
        projectId: null, // 未分類＝本人限定（非公開）が既定
        status: 'todo',
        type: 'respond',
        due: '',
      });
      setTasks((prev) => (prev ? [created, ...prev] : [created])); // 即反映
      setQuickTitle(''); // 入力欄クリア
    } catch (err) {
      // 失敗は UI を壊さない（秘密は出さない）。
      console.warn('[Home] クイック追加で例外', err instanceof Error ? err.message : err);
    } finally {
      setQuickBusy(false);
    }
  };

  const loading = tasks === null;
  const [yearNum, monthNum] = TODAY.split('-').map(Number);

  return (
    <div className="fade-up space-y-5">
      {/* greeting */}
      <div>
        <p className="text-xs text-slate-sub">{todayLabel()}</p>
        <h1 className="text-xl font-bold text-ink mt-0.5">
          おはようございます、{viewer.name} さん
        </h1>
        <p className="text-sm text-slate-sub mt-1">
          今日の予定 <span className="font-num font-semibold text-charcoal">{todayEvents.length}</span> 件、
          未対応のタスク <span className="font-num font-semibold text-charcoal">{openCount}</span> 件
          {dueTodayCount > 0 && (
            <>
              （うち<span className="font-num font-semibold text-danger-deep">{dueTodayCount}</span>件は今日まで）
            </>
          )}
          です。
        </p>
      </div>

      {/* 中段：ミニ月間カレンダー（2/3）＋チームタスク・自分のToDo（1/3） */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* ミニ月間カレンダー（当月固定・クリックで /calendar へ） */}
        <div className="lg:col-span-2">
          <SectionCard
            title="カレンダー"
            to="/calendar"
            headerExtra={
              <div className="inline-flex items-center rounded-soft border border-hair bg-white p-0.5">
                {([
                  ['day', '日'],
                  ['week', '週'],
                  ['month', '月'],
                ] as const).map(([k, lbl]) => (
                  <button
                    key={k}
                    onClick={() => switchCalView(k)}
                    title={
                      k === 'day'
                        ? '今日の予定を時刻順のリストで表示'
                        : k === 'week'
                          ? '今週の予定をチップで表示'
                          : '当月全体をドットで表示'
                    }
                    className={
                      'inline-flex items-center h-6 px-2 rounded-[6px] text-xs font-medium transition ' +
                      (calView === k ? 'bg-charcoal text-white shadow-sm' : 'text-slate-sub hover:text-charcoal')
                    }
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            }
          >
            <Link to="/calendar" className="block rounded-soft -mx-1 px-1 hover:bg-base/40 transition" aria-label="カレンダーを開く">
              {calView === 'month' ? (
                <>
                  <p className="font-num text-xs font-semibold text-charcoal">
                    {yearNum}年{monthNum}月
                  </p>
                  <div className="mt-2 grid grid-cols-7 text-center">
                    {WEEKDAY_JP.map((w) => (
                      <span key={w} className="text-2xs font-medium text-slate-400 py-1">
                        {w}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {cells.map((ymd, i) => {
                      if (!ymd) return <div key={'blank-' + i} />;
                      const dots = dayDots.get(ymd) || [];
                      return (
                        <div key={ymd} className="flex flex-col items-center gap-0.5 py-1.5">
                          <span
                            className={`font-num text-xs w-7 h-7 grid place-items-center rounded-full ${
                              ymd === TODAY ? 'bg-charcoal text-white font-semibold' : 'text-charcoal'
                            }`}
                          >
                            {Number(ymd.slice(8))}
                          </span>
                          {/* ドットは最大5（狭い画面では2段に折り返し可）＋超過は小さな「+N」 */}
                          <span className="flex flex-wrap items-center justify-center gap-0.5 min-h-[6px] max-w-[40px]">
                            {dots.slice(0, 5).map((c, j) => (
                              <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
                            ))}
                            {dots.length > 5 && (
                              <span className="text-[9px] leading-none text-slate-400">+{dots.length - 5}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : calView === 'day' ? (
                <>
                  {/* 日表示: 今日1日の詳細リスト（時刻順・週チップと同じソース＝dayItems の当日分）。
                      終日・時刻なしは週ビューのソート規則と同じく末尾（「終日」表記）。
                      件数制限なし・多い日は max-h ＋内部スクロールでカードが伸びすぎないようにする。 */}
                  <p className="font-num text-xs font-semibold text-charcoal">{todayLabel()}</p>
                  <div className="mt-2 max-h-64 overflow-y-auto space-y-1 pr-0.5">
                    {todayItems.length === 0 ? (
                      <p className="text-xs text-slate-sub py-2">今日の予定はありません</p>
                    ) : (
                      todayItems.map((it, j) => (
                        <div key={j} className="flex items-center gap-2 min-w-0">
                          <span className="shrink-0 w-12 font-num text-2xs text-slate-sub text-right">
                            {it.time || '終日'}
                          </span>
                          <span
                            className="block flex-1 min-w-0 truncate rounded-[4px] px-1.5 py-1 text-xs font-medium leading-tight"
                            style={{ background: it.bg, color: it.fg }}
                          >
                            {it.title}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* 週表示（sm以上）: 今週7日を横1行・各日はミニチップ最大5件＋「+N」 */}
                  <div className="hidden sm:grid grid-cols-7 gap-1">
                    {weekDays.map((ymd, i) => {
                      const isToday = ymd === TODAY;
                      const items = dayItems.get(ymd) || [];
                      return (
                        <div
                          key={ymd}
                          className={`flex flex-col items-center gap-1 rounded-soft px-0.5 py-1.5 ${isToday ? 'bg-base/60' : ''}`}
                        >
                          <span className={`text-2xs ${isToday ? 'font-bold text-charcoal' : 'font-medium text-slate-400'}`}>
                            {isToday ? '今日' : WEEKDAY_JP[i]}
                          </span>
                          <span
                            className={`font-num text-xs w-7 h-7 grid place-items-center rounded-full ${
                              isToday ? 'bg-charcoal text-white font-semibold' : 'text-charcoal'
                            }`}
                          >
                            {Number(ymd.slice(8))}
                          </span>
                          <div className="w-full space-y-0.5">
                            {items.slice(0, 5).map((it, j) => (
                              <span
                                key={j}
                                className="block w-full truncate rounded-[4px] px-1 py-0.5 text-2xs font-medium leading-tight"
                                style={{ background: it.bg, color: it.fg }}
                              >
                                {it.title}
                              </span>
                            ))}
                            {items.length > 5 && (
                              <span className="block px-1 text-2xs text-slate-400">+{items.length - 5}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* 週表示（sm未満）: 7列は窮屈なので「今日〜週末」の縦積みリストで崩さない */}
                  <div className="sm:hidden space-y-2">
                    {weekDays
                      .filter((ymd) => ymd >= TODAY)
                      .map((ymd) => {
                        const isToday = ymd === TODAY;
                        const items = dayItems.get(ymd) || [];
                        return (
                          <div key={ymd} className="flex items-start gap-2.5">
                            <span
                              className={`shrink-0 w-16 pt-0.5 font-num text-2xs ${
                                isToday ? 'font-bold text-charcoal' : 'text-slate-sub'
                              }`}
                            >
                              {isToday ? '今日' : dateHeading(ymd)}
                            </span>
                            <div className="min-w-0 flex-1 space-y-0.5">
                              {items.length === 0 ? (
                                <span className="text-2xs text-slate-400">予定なし</span>
                              ) : (
                                <>
                                  {items.slice(0, 5).map((it, j) => (
                                    <span
                                      key={j}
                                      className="block w-full truncate rounded-[4px] px-1 py-0.5 text-2xs font-medium leading-tight"
                                      style={{ background: it.bg, color: it.fg }}
                                    >
                                      {it.title}
                                    </span>
                                  ))}
                                  {items.length > 5 && (
                                    <span className="block px-1 text-2xs text-slate-400">+{items.length - 5}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
              {/* 凡例（CAL_KIND ＋ 自分のタスク期限・週/月共通） */}
              <div className="mt-2 pt-2 border-t border-hair flex flex-wrap items-center gap-x-4 gap-y-1">
                {(['meeting', 'deadline', 'plan'] as const).map((k) => (
                  <span key={k} className="inline-flex items-center gap-1.5 text-2xs text-slate-sub">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: CAL_KIND[k].dot }} />
                    {CAL_KIND[k].label}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5 text-2xs text-slate-sub">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: MY_DUE_DOT }} />
                  自分のタスク期限
                </span>
              </div>
            </Link>
          </SectionCard>
        </div>

        {/* 右カラム：Meet取り込み → 自分のToDo → チームタスク（2026-07-10 並び替え：自分ごとを上に） */}
        <div className="space-y-4">
          {/* Meet取り込み（compact）：未処理でDocありの会議のみ最大3件。
              未連携・対象0件のときはパネル自体が非表示（コンポーネント側で null を返す）。 */}
          <MeetImportPanel variant="compact" />

          {/* 自分のToDo（クイック追加＋due昇順・最大5件） */}
          <SectionCard title="自分のToDo" to="/tasks">
            {/* クイック追加：/tasks と同じ見た目・同じ挙動（Enter or 追加ボタン） */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={quickTitle}
                onChange={(e) => setQuickTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addQuickTask();
                  }
                }}
                placeholder="タスクを入力して Enter で追加"
                className="h-9 flex-1 min-w-0 px-3 rounded-soft border border-hair bg-white text-sm text-charcoal placeholder:text-slate-sub focus:border-sky-brand focus:outline-none"
              />
              <button
                onClick={() => void addQuickTask()}
                disabled={!quickTitle.trim() || quickBusy}
                title="自分の未分類タスクとして追加します（自分だけに表示）"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-soft bg-charcoal text-white text-sm font-medium hover:bg-charcoal-700 transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Icon name="check" size={13} /> 追加
              </button>
            </div>
            <div className="mt-3">
              {loading ? (
                <Empty text="読み込み中…" />
              ) : myOpen.length === 0 ? (
                <Empty text="未対応のタスクはありません。" />
              ) : (
                <ul className="space-y-1.5">
                  {myOpen.map((t) => {
                    const overdue = !!t.due && t.due < TODAY;
                    return (
                      <li key={t.id} className="flex items-center gap-2.5 min-w-0">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: PROJECT_MAP[t.projectId]?.color || '#94A3B8' }}
                        />
                        <span className="text-sm text-charcoal truncate">{t.title}</span>
                        <span
                          className={`ml-auto font-num text-2xs shrink-0 ${
                            overdue ? 'text-danger font-semibold' : 'text-slate-400'
                          }`}
                        >
                          {t.due ? t.due.slice(5).replace('-', '/') : '期限なし'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </SectionCard>

          {/* チームタスク（直近1週間・日付グルーピング・期限超過を先頭） */}
          <SectionCard title="チームタスク" to={execView ? '/members' : '/tasks'}>
            {loading ? (
              <Empty text="読み込み中…" />
            ) : teamTasks.overdue.length === 0 && teamTasks.byDate.length === 0 ? (
              <Empty text="直近1週間の期限付きタスクはありません。" />
            ) : (
              <div className="space-y-3">
                {teamTasks.overdue.length > 0 && (
                  <div>
                    <p className="text-2xs font-bold text-danger-deep flex items-center gap-1.5">
                      <Icon name="circle-alert" size={12} />
                      期限超過 <span className="font-num">{teamTasks.overdue.length}</span>
                    </p>
                    <ul className="mt-1.5 space-y-1.5">
                      {teamTasks.overdue.slice(0, 6).map((t) => (
                        <TeamTaskRow key={t.id} task={t} overdue />
                      ))}
                    </ul>
                    {teamTasks.overdue.length > 6 && (
                      <p className="mt-1 text-2xs text-slate-400">他 {teamTasks.overdue.length - 6} 件</p>
                    )}
                  </div>
                )}
                {teamTasks.byDate.map((g) => (
                  <div key={g.date}>
                    <p className={`text-2xs font-bold ${g.date === TODAY ? 'text-charcoal' : 'text-slate-sub'}`}>
                      {g.date === TODAY ? '今日' : dateHeading(g.date)}
                    </p>
                    <ul className="mt-1.5 space-y-1.5">
                      {g.items.map((t) => (
                        <TeamTaskRow key={t.id} task={t} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* 下段：既存セクション（新着議事録・プロジェクト進捗） */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 新着議事録 */}
        <SectionCard title="新着の議事録" to="/notes">
          {notesRaw === null ? (
            <Empty text="読み込み中…" />
          ) : recentNotes.length === 0 ? (
            <Empty text="表示できる議事録はありません。" />
          ) : (
            <ul className="space-y-2.5">
              {recentNotes.map((n) => {
                const src = SOURCE_META[n.source];
                const pj = PROJECT_MAP[n.projectId];
                return (
                  <li key={n.id}>
                    <Link to={`/notes/${n.id}`} className="block rounded-soft -mx-1 px-1 py-0.5 hover:bg-base/50">
                      <div className="flex items-start gap-3">
                        <span className="w-2 h-2 mt-1.5 rounded-full shrink-0" style={{ background: pj?.color || '#94A3B8' }} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-charcoal truncate">{n.title}</div>
                          {n.summary && (
                            <div className="mt-0.5 text-2xs text-slate-sub line-clamp-1">{n.summary}</div>
                          )}
                          <div className="mt-0.5 flex items-center gap-2 text-2xs text-slate-400">
                            <span className="font-num">{n.date}</span>
                            <span className="rounded-full px-1.5 py-0.5" style={{ background: src.bg, color: src.fg }}>
                              {src.label}
                            </span>
                            {n.participants.length > 0 && (
                              <span className="truncate">
                                {n.participants
                                  .map((id) => MEMBER_MAP[id]?.name || id)
                                  .slice(0, 2)
                                  .join('・')}
                                {n.participants.length > 2 ? ` 他${n.participants.length - 2}名` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        {/* プロジェクト進捗（KGI / KPI） */}
        <SectionCard title="プロジェクトの進捗" to="/projects">
          {projects.length === 0 ? (
            <Empty text="表示できるプロジェクトはありません。" />
          ) : (
            <ul className="space-y-3">
              {projects.map((p) => {
                const topKpi = p.kpis[0];
                const ach = kpiAchievement(topKpi);
                return (
                  <li key={p.id}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color }} />
                        <span className="text-sm font-medium text-charcoal truncate">{p.name}</span>
                      </div>
                      {/* 値はテキスト色で読み手に強く（dataviz: 系列色の文字を作らない） */}
                      <span className="shrink-0 text-xs text-slate-sub">
                        KGI <span className="font-num font-bold text-charcoal">{p.kgiProgress}%</span>
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-soft overflow-hidden">
                      <div
                        className="bar-grow h-full rounded-full"
                        style={{ width: `${p.kgiProgress}%`, background: p.color }}
                      />
                    </div>
                    {topKpi && (
                      <div className="mt-1 text-2xs text-slate-400">
                        {topKpi.label}：
                        <span className="font-num text-slate-500"> {topKpi.value}{topKpi.unit}</span>
                        <span className="text-slate-400">（{topKpi.target}{ach ? `・達成率 ${ach.pct}%` : ''}）</span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* モジュールショートカット */}
      <section>
        <h2 className="text-sm font-bold text-charcoal mb-2.5">モジュール</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {SHORTCUTS.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="card-hover rounded-card border border-hair bg-white p-3.5 block"
            >
              <div className="text-sm font-semibold text-charcoal">{s.label}</div>
              <div className="mt-0.5 text-2xs text-slate-sub leading-snug">{s.desc}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
